import { Injectable } from '@angular/core';
import { MapLayersManager } from '../map-layers-manager';
import { FeatureCollection } from 'geojson';
import { ConfigurationModel } from '../../../models/configuration.model';
import { Subscription } from 'rxjs';
import { LocationApiService } from '../../../../data/location-api/location-api.service';
import moment from 'moment';
import { ToastService } from '../../../services/toast.service';
import { CircleLayerSpecification, LineLayerSpecification, LngLat, SymbolLayerSpecification } from 'maplibre-gl';
import { LocationSearchParams } from '../../../models/breadcrumb.model';

@Injectable({
  providedIn: 'root',
})
export class TracksDebugLayerService {
  static readonly TRACK_LINE_DEBUG_SOURCE_ID = 'track-line-debug';
  static readonly TRACK_POINT_DEBUG_SOURCE_ID = 'track-point-debug';
  static readonly LAYER_ID_TRACK_LINE_DEBUG = 'track-line-debug';
  static readonly LAYER_ID_TRACK_POINT_DEBUG = 'track-point-debug';

  private mapLayersManager: MapLayersManager;
  private lineSourceData: FeatureCollection;
  private pointSourceData: FeatureCollection;
  hours: number;
  defaultHoursAgoFilter: number;

  private readonly openSubscriptions = Array<Subscription>();

  constructor(
    private readonly locationApi: LocationApiService,
    private readonly toast: ToastService
  ) {}

  init(mapLayersManager: MapLayersManager, configuration: ConfigurationModel, isEnabled: boolean) {
    if (!!this.mapLayersManager) {
      throw Error('The map layers manager has already been set.');
    }
    this.mapLayersManager = mapLayersManager;
    this.defaultHoursAgoFilter = configuration.lookBackPeriod;
    this.hours = configuration.lookBackPeriod;
    this.addSourcesAndLayers();
  }

  release() {
    if (!this.mapLayersManager) {
      throw Error('The map has not been set!');
    }

    for (const subscription of this.openSubscriptions) {
      subscription.unsubscribe();
    }
    this.openSubscriptions.length = 0;
    this.mapLayersManager = null;
  }

  addSourcesAndLayers() {
    this.mapLayersManager.addSource(TracksDebugLayerService.TRACK_LINE_DEBUG_SOURCE_ID, {
      type: 'geojson',
      data: {
        type: 'FeatureCollection',
        features: [],
      },
    });

    this.mapLayersManager.addSource(TracksDebugLayerService.TRACK_POINT_DEBUG_SOURCE_ID, {
      type: 'geojson',
      data: {
        type: 'FeatureCollection',
        features: [],
      },
    });

    const lineLayer = {
      id: TracksDebugLayerService.LAYER_ID_TRACK_LINE_DEBUG,
      source: TracksDebugLayerService.TRACK_LINE_DEBUG_SOURCE_ID,
      type: 'line',
      layout: {
        'line-join': 'round',
        'line-cap': 'round',
      },
      paint: {
        'line-color': '#ffff7f',
        'line-opacity': 1.0,
        'line-width': 3,
      },
    };
    this.mapLayersManager.addLayer(
      lineLayer as LineLayerSpecification | SymbolLayerSpecification,
      this.mapLayersManager.firstSymbolLayerId
    );

    const pointLayer = {
      id: TracksDebugLayerService.LAYER_ID_TRACK_POINT_DEBUG,
      source: TracksDebugLayerService.TRACK_POINT_DEBUG_SOURCE_ID,
      type: 'circle',
      minzoom: 15,
      paint: {
        'circle-color': '#ffb76f',
        'circle-radius': 3,
      }
    } as CircleLayerSpecification;
    this.mapLayersManager.addLayer(
      pointLayer,
      this.mapLayersManager.firstSymbolLayerId,
    );
  }

  async updateLineSource() {
    try {
      const response = await this.locationApi.getTrackGeometries(
        null,
        null,
        moment().subtract(this.defaultHoursAgoFilter, 'hour').toDate(),
        null
      );
      this.lineSourceData = response;
      this.mapLayersManager?.getGeoJsonSource(TracksDebugLayerService.TRACK_LINE_DEBUG_SOURCE_ID)?.setData(response);
    } catch (error) {
      this.toast.short('Error loading raw track segment data!');
    }
  }

  async updatePointSource(
    center: LngLat,
    zoomLevel: number,
  ) {
    const radius = this.zoomToRadiusMeters(zoomLevel, center.lat);
    const searchParams = {
      timeFrom: moment().subtract(this.defaultHoursAgoFilter, 'hour').unix(),
      timeTo: Math.floor(new Date().getTime() / 1000),
      coordinate: {latitude: center.lat, longitude: center.lng},
      searchLimit: 1000, // max allowed
      radius: radius > 5000 ? 5000 : radius,
      locationSources: undefined,
      sessions: undefined,
    } as LocationSearchParams;
    try {
      const response = await this.locationApi.searchGeobuf(searchParams);
      this.pointSourceData = response;
      this.mapLayersManager?.getGeoJsonSource(TracksDebugLayerService.TRACK_POINT_DEBUG_SOURCE_ID)?.setData(response);
    } catch (error) {
      this.toast.short('Error loading raw point data!');
    }
  }

  private zoomToRadiusMeters(zoom: number, latitude: number, width = 1200, height = 900) {
    const metersPerPixel = 156543.03392 * Math.cos(latitude * Math.PI / 180) / Math.pow(2, zoom);
    const diagonalPx = Math.sqrt(width ** 2 + height ** 2);
    return (diagonalPx / 2) * metersPerPixel;
  }

  clearSources() {
    this.lineSourceData = null;
    this.mapLayersManager?.getGeoJsonSource(TracksDebugLayerService.TRACK_LINE_DEBUG_SOURCE_ID)?.setData({
      type: 'FeatureCollection',
      features: [],
    });

    this.pointSourceData = null;
    this.mapLayersManager?.getGeoJsonSource(TracksDebugLayerService.TRACK_POINT_DEBUG_SOURCE_ID)?.setData({
      type: 'FeatureCollection',
      features: [],
    });
  }
}
